import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { AnimatedCurrency, CostTicker, BenchmarkBar, CultureImprovementSimulator, CultureLeagueTable } from "./GamificationComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Industry-specific messaging and imagery
const industryData = {
  manufacturing: {
    example: "manufacturing environments",
    challenge: "physical demands and shift work patterns",
    statistic: "Manufacturing workers take 25% more sick days than the national average",
    imagery: "🏭",
    heroImage: "https://images.unsplash.com/photo-1700727448686-b314cb5f9948?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtYW51ZmFjdHVyaW5nJTIwd29ya2Vyc3xlbnwwfHx8fDE3NTM3MDMwMTJ8MA&ixlib=rb-4.1.0&q=85",
    color: "#dc2626",
    lightColor: "#fef2f2",
    avgTurnoverRate: 0.18,
    avgRecruitWeeks: 8,
    avgSickDaysPerEmployee: 4.5,
    avgMentalHealthPct: 30
  },
  construction: {
    example: "construction sites", 
    challenge: "high-risk work environments and project pressures",
    statistic: "Construction has the highest rate of workplace mental health issues",
    imagery: "🏗️",
    heroImage: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzfGVufDB8fHx8MTc1MzcwMzAxNnww&ixlib=rb-4.1.0&q=85",
    color: "#ea580c",
    lightColor: "#fff7ed",
    avgTurnoverRate: 0.20,
    avgRecruitWeeks: 6,
    avgSickDaysPerEmployee: 4.0,
    avgMentalHealthPct: 32
  },
  transport: {
    example: "transport operations",
    challenge: "irregular hours and time away from home",
    statistic: "Transport workers report 30% higher stress levels than other industries",
    imagery: "🚛",
    heroImage: "https://images.unsplash.com/photo-1574757974346-45bae947d89a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHx0cnVjayUyMGRyaXZlcnxlbnwwfHx8fDE3NTM3MDMwMjF8MA&ixlib=rb-4.1.0&q=85",
    color: "#0ea5e9",
    lightColor: "#f0f9ff",
    avgTurnoverRate: 0.22,
    avgRecruitWeeks: 6,
    avgSickDaysPerEmployee: 5.0,
    avgMentalHealthPct: 30
  },
  engineering: {
    example: "engineering teams",
    challenge: "technical complexity and deadline pressures",
    statistic: "Engineering professionals show higher rates of burnout",
    imagery: "⚙️",
    heroImage: "https://images.unsplash.com/photo-1748348209623-906c42dd1f7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxlbmdpbmVlcnMlMjB3b3JrcGxhY2V8ZW58MHx8fHwxNzUzNzAzMDI2fDA&ixlib=rb-4.1.0&q=85",
    color: "#059669",
    lightColor: "#f0fdf4",
    avgTurnoverRate: 0.13,
    avgRecruitWeeks: 10,
    avgSickDaysPerEmployee: 3.5,
    avgMentalHealthPct: 28
  },
  utilities: {
    example: "utilities sector",
    challenge: "critical infrastructure responsibilities",
    statistic: "Utilities workers face unique stress from maintaining essential services",
    imagery: "⚡",
    heroImage: "https://images.unsplash.com/photo-1741418566305-8568a8a6f221?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHx1dGlsaXR5JTIwd29ya2Vyc3xlbnwwfHx8fDE3NTM3MDMwMzF8MA&ixlib=rb-4.1.0&q=85",
    color: "#7c3aed",
    lightColor: "#faf5ff",
    avgTurnoverRate: 0.12,
    avgRecruitWeeks: 10,
    avgSickDaysPerEmployee: 3.8,
    avgMentalHealthPct: 26
  },
  wind: {
    example: "wind energy operations",
    challenge: "remote locations and weather-dependent work conditions",
    statistic: "Wind energy workers face unique challenges with offshore and remote site work",
    imagery: "💨",
    heroImage: "https://images.unsplash.com/photo-1548337138-e87d889cc369?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHx3aW5kJTIwdHVyYmluZXN8ZW58MHx8fHwxNzUzNzAzODM3fDA&ixlib=rb-4.1.0&q=85",
    color: "#0d9488",
    lightColor: "#f0fdfa",
    avgTurnoverRate: 0.14,
    avgRecruitWeeks: 10,
    avgSickDaysPerEmployee: 3.5,
    avgMentalHealthPct: 26
  },
  energy: {
    example: "energy sector operations",
    challenge: "high-risk environments and shift work patterns",
    statistic: "Energy sector workers have elevated stress levels due to critical infrastructure demands",
    imagery: "🔋",
    heroImage: "https://images.unsplash.com/photo-1595856619767-ab739fa7daae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxlbmVyZ3klMjB3b3JrZXJzfGVufDB8fHx8MTc1MzcwMzg0NHww&ixlib=rb-4.1.0&q=85",
    color: "#dc2626",
    lightColor: "#fef2f2",
    avgTurnoverRate: 0.12,
    avgRecruitWeeks: 10,
    avgSickDaysPerEmployee: 3.8,
    avgMentalHealthPct: 28
  },
  other: {
    example: "your industry",
    challenge: "workplace pressures and demands",
    statistic: "Every industry can benefit from improved workplace wellbeing",
    imagery: "🏢",
    heroImage: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjB3b3JrZXJzfGVufDB8fHx8MTc1MzcwMzAzNnww&ixlib=rb-4.1.0&q=85",
    color: "#6b7280",
    lightColor: "#f9fafb",
    avgTurnoverRate: 0.15,
    avgRecruitWeeks: 8,
    avgSickDaysPerEmployee: 3.9,
    avgMentalHealthPct: 28
  }
};

function App() {
  // Steps: 0=Industry, 1=Company, 2=Turnover, 3=SickDays, 4=Presenteeism, 5=Results
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    industry: "",
    total_employees: 50,
    average_salary: 30000,
    employees_left: 5,
    recruitment_weeks: 6,
    sick_days: 20,
    mental_health_percentage: 30,
    has_presenteeism: false
  });

  // Track which fields use industry averages
  const [useAverage, setUseAverage] = useState({
    employees_left: false,
    recruitment_weeks: false,
    sick_days: false,
    mental_health_percentage: false
  });

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const utmCampaign = urlParams.get('utm_campaign');
    if (utmSource || utmMedium || utmCampaign) {
      trackVisit({ utm_source: utmSource, utm_medium: utmMedium, utm_campaign: utmCampaign, referrer: document.referrer, timestamp: new Date().toISOString() });
    }
    trackStepProgress(0, 'started');
  }, []);

  const trackVisit = async (trackingData) => {
    try { await axios.post(`${API}/track-visit`, trackingData); } catch (e) { /* silent */ }
  };
  const trackStepProgress = async (step, action = 'reached') => {
    try { await axios.post(`${API}/track-step`, { step, action, timestamp: new Date().toISOString(), user_agent: navigator.userAgent }); } catch (e) { /* silent */ }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // If user manually changes a field, disable its "use average" flag
    if (useAverage[field]) {
      setUseAverage(prev => ({ ...prev, [field]: false }));
    }
  };

  const getIndustryAverages = () => {
    const ind = industryData[formData.industry] || industryData.other;
    const employees = formData.total_employees;
    return {
      employees_left: Math.max(1, Math.round(employees * ind.avgTurnoverRate)),
      recruitment_weeks: ind.avgRecruitWeeks,
      sick_days: Math.min(200, Math.round(employees * ind.avgSickDaysPerEmployee)),
      mental_health_percentage: ind.avgMentalHealthPct
    };
  };

  const toggleAverage = (field) => {
    const newVal = !useAverage[field];
    setUseAverage(prev => ({ ...prev, [field]: newVal }));
    if (newVal) {
      const averages = getIndustryAverages();
      setFormData(prev => ({ ...prev, [field]: averages[field] }));
    }
  };

  const nextStep = () => {
    if (currentStep < 4) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      trackStepProgress(newStep, 'reached');
    }
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const calculateResults = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/calculate-culture-cost`, {
        industry: formData.industry,
        total_employees: formData.total_employees,
        employees_left: formData.employees_left,
        average_salary: formData.average_salary,
        recruitment_weeks: formData.recruitment_weeks,
        sick_days: formData.sick_days,
        mental_health_percentage: formData.mental_health_percentage,
        has_presenteeism: formData.has_presenteeism
      });
      setResults(response.data);
      setCurrentStep(5);
      trackStepProgress(5, 'completed');
    } catch (err) {
      setError('Unable to calculate costs at this time. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(amount);

  const calculatePotentialSavings = (r) => ({
    workshopROI: Math.max(1, Math.min(5, Math.floor(r.absence_cost / 175))),
    managerTrainingSavings: Math.floor(r.turnover_cost * 0.15),
    wellbeingProgramSavings: Math.floor(r.total_cost * 0.25)
  });

  // "I don't know" toggle button component
  const DontKnowToggle = ({ field, label }) => (
    <button
      onClick={() => toggleAverage(field)}
      data-testid={`dont-know-${field}`}
      className={`mt-3 text-sm px-4 py-2 rounded-full border transition-all ${
        useAverage[field]
          ? 'bg-amber-50 border-amber-300 text-amber-700 font-medium'
          : 'bg-white border-slate-200 text-slate-500 hover:border-amber-300 hover:text-amber-600'
      }`}
    >
      {useAverage[field] ? `Using industry average` : label || "I don't know — use industry average"}
    </button>
  );

  // Progress bar
  const ProgressBar = () => {
    const totalFormSteps = 4; // steps 0-4 (industry through presenteeism)
    const stepsRemaining = Math.max(0, totalFormSteps - currentStep);
    return (
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-slate-600">Step {currentStep + 1} of {totalFormSteps + 1}</span>
          {stepsRemaining > 0 && (
            <span className="text-sm text-amber-600 font-medium">
              {stepsRemaining === 1 ? 'Last step!' : `${stepsRemaining} steps remaining`}
            </span>
          )}
        </div>
        <div className="w-full bg-slate-200 rounded-full h-3">
          <div className="h-3 rounded-full transition-all duration-500 ease-out" style={{ width: `${(currentStep / totalFormSteps) * 100}%`, background: 'linear-gradient(90deg, #facf39 0%, #f9c307 100%)' }}></div>
        </div>
      </div>
    );
  };

  // Step 0: Industry Selection (first screen — no welcome)
  if (currentStep === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{color: '#333333'}} data-testid="industry-heading">
              Which industry best describes your organisation?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {Object.entries(industryData).map(([key, data]) => (
                <button
                  key={key}
                  data-testid={`industry-${key}`}
                  onClick={() => {
                    handleInputChange('industry', key);
                    // Apply averages for any fields that have "use average" toggled
                    setTimeout(() => {
                      setCurrentStep(1);
                      trackStepProgress(1, 'reached');
                    }, 300);
                  }}
                  className={`relative p-5 rounded-xl border-2 transition-all duration-200 ${
                    formData.industry === key
                      ? 'border-yellow-400 bg-yellow-50 shadow-lg scale-105'
                      : 'border-slate-200 hover:border-yellow-300 hover:shadow-md'
                  }`}
                >
                  <div className="text-center">
                    <img src={data.heroImage} alt={key} className="w-14 h-14 mx-auto rounded-lg object-cover mb-2" />
                    <div className="font-semibold text-slate-800 capitalize text-sm">
                      {key === 'transport' ? 'Transport & Logistics' : key}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Company Profile
  if (currentStep === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <ProgressBar />
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{color: '#333333'}}>Tell us about your organisation</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-6 text-slate-700">How many people do you employ?</h3>
                <div className="text-6xl font-bold mb-4" style={{ color: '#333333' }}>{formData.total_employees}</div>
                <p className="text-slate-600">employees</p>
                <input type="range" min="1" max="500" value={formData.total_employees} onChange={(e) => handleInputChange('total_employees', parseInt(e.target.value))} className="w-full slider mt-4 mb-2" />
                <div className="flex justify-between text-sm text-slate-500"><span>1</span><span>500+</span></div>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-6 text-slate-700">What's the average annual salary?</h3>
                <div className="text-6xl font-bold mb-4" style={{ color: '#333333' }}>{formatCurrency(formData.average_salary)}</div>
                <p className="text-slate-600">per employee</p>
                <input type="range" min="15000" max="80000" step="1000" value={formData.average_salary} onChange={(e) => handleInputChange('average_salary', parseInt(e.target.value))} className="w-full slider mt-4 mb-2" />
                <div className="flex justify-between text-sm text-slate-500"><span>£15k</span><span>£80k+</span></div>
              </div>
            </div>
            <div className="flex justify-between mt-12">
              <button onClick={prevStep} className="bg-slate-200 text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors">Back</button>
              <button onClick={nextStep} className="text-white px-8 py-4 rounded-lg text-lg font-medium hover:opacity-90 transition-all" style={{ background: '#333333' }}>Continue</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Turnover & Recruitment — with "I don't know"
  if (currentStep === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <ProgressBar />
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{color: '#333333'}}>Staff turnover & recruitment</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-6 text-slate-700">How many employees left in the last 12 months?</h3>
                <div className="text-6xl font-bold mb-4" style={{ color: useAverage.employees_left ? '#d97706' : '#333333' }}>{formData.employees_left}</div>
                <p className="text-slate-600">employees</p>
                <input type="range" min="0" max="100" value={formData.employees_left} onChange={(e) => handleInputChange('employees_left', parseInt(e.target.value))} className="w-full slider mt-4 mb-2" disabled={useAverage.employees_left} style={{ opacity: useAverage.employees_left ? 0.5 : 1 }} />
                <div className="flex justify-between text-sm text-slate-500"><span>0</span><span>100+</span></div>
                <DontKnowToggle field="employees_left" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-6 text-slate-700">How many weeks to recruit & train a replacement?</h3>
                <div className="text-6xl font-bold mb-4" style={{ color: useAverage.recruitment_weeks ? '#d97706' : '#333333' }}>{formData.recruitment_weeks}</div>
                <p className="text-slate-600">weeks</p>
                <input type="range" min="1" max="12" value={formData.recruitment_weeks} onChange={(e) => handleInputChange('recruitment_weeks', parseInt(e.target.value))} className="w-full slider mt-4 mb-2" disabled={useAverage.recruitment_weeks} style={{ opacity: useAverage.recruitment_weeks ? 0.5 : 1 }} />
                <div className="flex justify-between text-sm text-slate-500"><span>1 week</span><span>12+ weeks</span></div>
                <DontKnowToggle field="recruitment_weeks" />
              </div>
            </div>
            <div className="flex justify-between mt-12">
              <button onClick={prevStep} className="bg-slate-200 text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors">Back</button>
              <button onClick={nextStep} className="text-white px-8 py-4 rounded-lg text-lg font-medium hover:opacity-90 transition-all" style={{ background: '#333333' }}>Continue</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Sick Days & Mental Health — with "I don't know"
  if (currentStep === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <ProgressBar />
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{color: '#333333'}}>Workplace absence patterns</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-6 text-slate-700">How many sick days were taken in the last 6 months?</h3>
                <div className="text-6xl font-bold mb-4" style={{ color: useAverage.sick_days ? '#d97706' : '#333333' }}>{formData.sick_days}</div>
                <p className="text-slate-600">days</p>
                <input type="range" min="0" max="200" value={formData.sick_days} onChange={(e) => handleInputChange('sick_days', parseInt(e.target.value))} className="w-full slider mt-4 mb-2" disabled={useAverage.sick_days} style={{ opacity: useAverage.sick_days ? 0.5 : 1 }} />
                <div className="flex justify-between text-sm text-slate-500"><span>0</span><span>200+</span></div>
                <DontKnowToggle field="sick_days" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-6 text-slate-700">What percentage were due to mental health?</h3>
                <div className="text-6xl font-bold mb-4" style={{ color: useAverage.mental_health_percentage ? '#d97706' : '#333333' }}>{formData.mental_health_percentage}%</div>
                <input type="range" min="0" max="50" value={formData.mental_health_percentage} onChange={(e) => handleInputChange('mental_health_percentage', parseInt(e.target.value))} className="w-full slider mt-4 mb-2" disabled={useAverage.mental_health_percentage} style={{ opacity: useAverage.mental_health_percentage ? 0.5 : 1 }} />
                <div className="flex justify-between text-sm text-slate-500 mb-1"><span>0%</span><span>50%+</span></div>
                <DontKnowToggle field="mental_health_percentage" />
              </div>
            </div>
            <div className="flex justify-between mt-12">
              <button onClick={prevStep} className="bg-slate-200 text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors">Back</button>
              <button onClick={nextStep} className="text-white px-8 py-4 rounded-lg text-lg font-medium hover:opacity-90 transition-all" style={{ background: '#333333' }}>Continue</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 4: Presenteeism + Contact combined
  if (currentStep === 4) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <ProgressBar />
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-6" style={{color: '#333333'}}>
                Do you notice employees working while unwell?
              </h2>
              <p className="text-slate-600 mb-6">
                This includes people coming to work despite being physically or mentally unwell, often with reduced productivity.
              </p>
              <div className="flex justify-center space-x-6 mb-8">
                <button
                  data-testid="presenteeism-yes"
                  onClick={() => handleInputChange('has_presenteeism', true)}
                  className={`px-8 py-4 rounded-lg border-2 transition-all ${formData.has_presenteeism ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-blue-300'}`}
                >
                  <div className="text-2xl mb-2">👍</div>
                  <div className="font-medium">Yes</div>
                </button>
                <button
                  data-testid="presenteeism-no"
                  onClick={() => handleInputChange('has_presenteeism', false)}
                  className={`px-8 py-4 rounded-lg border-2 transition-all ${formData.has_presenteeism === false ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-blue-300'}`}
                >
                  <div className="text-2xl mb-2">👎</div>
                  <div className="font-medium">No</div>
                </button>
              </div>
            </div>
            <div className="flex justify-between mt-8">
              <button onClick={prevStep} className="bg-slate-200 text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors">Back</button>
              <button onClick={calculateResults} disabled={loading} data-testid="calculate-btn" className="text-white px-8 py-4 rounded-lg text-lg font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: '#333333' }}>
                {loading ? 'Calculating...' : 'Calculate My Costs'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 5: Results
  if (currentStep === 5 && results) {
    const savings = calculatePotentialSavings(results);
    const industryInfo = industryData[formData.industry];
    const turnoverRate = formData.total_employees > 0 ? (formData.employees_left / formData.total_employees * 100) : 0;
    const avgTurnoverRate = (industryInfo.avgTurnoverRate * 100);
    const sickDaysPerEmployee = formData.total_employees > 0 ? (formData.sick_days / formData.total_employees) : 0;
    const avgSickDaysPerEmployee = industryInfo.avgSickDaysPerEmployee;
    const costPerLeaver = formData.employees_left > 0 ? results.turnover_cost / formData.employees_left : 0;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-slate-800 mb-4">Your estimated annual cost is</h1>
              <div className="text-6xl font-bold text-blue-600 mb-6" data-testid="total-cost">
                <AnimatedCurrency amount={results.total_cost} />
              </div>
              <p className="text-slate-600 text-lg">Based on industry averages for {industryInfo.example}</p>
            </div>

            {/* Real-time cost ticker */}
            <CostTicker annualCost={results.total_cost} />

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
              <h3 className="font-medium text-amber-900 mb-2">{industryInfo.imagery} Industry Context</h3>
              <p className="text-amber-800">{industryInfo.statistic}. Common challenges include {industryInfo.challenge}.</p>
            </div>

            {/* Cost breakdown with animated figures */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                <h4 className="font-medium text-orange-900 mb-2">Recruitment & Training</h4>
                <div className="text-2xl font-bold text-orange-700 mb-2"><AnimatedCurrency amount={results.turnover_cost} /></div>
                <p className="text-sm text-orange-800">Cost of replacing {formData.employees_left} employees</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-medium text-yellow-900 mb-2">Mental Health Absence</h4>
                <div className="text-2xl font-bold text-yellow-700 mb-2"><AnimatedCurrency amount={results.absence_cost} /></div>
                <p className="text-sm text-yellow-800">Cost of {Math.round(results.mental_health_sick_days)} mental health-related sick days</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h4 className="font-medium text-purple-900 mb-2">Reduced Productivity</h4>
                <div className="text-2xl font-bold text-purple-700 mb-2"><AnimatedCurrency amount={results.presenteeism_cost} /></div>
                <p className="text-sm text-purple-800">{formData.has_presenteeism ? 'Estimated presenteeism costs' : 'No presenteeism identified'}</p>
              </div>
            </div>

            {/* Benchmark comparison */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-8" data-testid="benchmark-comparison">
              <h3 className="text-lg font-bold text-slate-800 mb-4">How You Compare to Industry Averages</h3>
              <BenchmarkBar label="Turnover rate" yourValue={parseFloat(turnoverRate.toFixed(1))} avgValue={parseFloat(avgTurnoverRate.toFixed(1))} unit="%" lowerIsBetter={true} />
              <BenchmarkBar label="Sick days per employee (6 months)" yourValue={parseFloat(sickDaysPerEmployee.toFixed(1))} avgValue={avgSickDaysPerEmployee} lowerIsBetter={true} />
              <BenchmarkBar label="Mental health absence" yourValue={formData.mental_health_percentage} avgValue={industryInfo.avgMentalHealthPct} unit="%" lowerIsBetter={true} />
            </div>

            {/* Industry league table */}
            <CultureLeagueTable industry={formData.industry} totalCost={results.total_cost} />

            {/* Improvement simulator */}
            <CultureImprovementSimulator
              currentLeavers={formData.employees_left}
              costPerLeaver={costPerLeaver}
              currentSickDays={formData.sick_days}
              dailySalary={results.daily_salary}
              annualCost={results.total_cost}
            />

            <div className="bg-green-50 border border-green-200 rounded-xl p-8 mb-8">
              <h3 className="text-xl font-medium text-green-900 mb-6">Potential Return on Wellbeing Investment</h3>
              <div className="space-y-4 text-green-800">
                <p>• <strong>Mental health education workshops (prices from £175/session)</strong> could prevent multiple absences, potentially saving {formatCurrency(savings.workshopROI * 175)}</p>
                <p>• <strong>Manager wellbeing training</strong> could reduce turnover by 15%, saving approximately {formatCurrency(savings.managerTrainingSavings)}</p>
                <p>• <strong>Comprehensive wellbeing programme</strong> typically delivers 25% cost reduction: {formatCurrency(savings.wellbeingProgramSavings)}</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-green-50 border border-blue-200 rounded-2xl p-8 mt-8">
              <div className="text-center mb-6">
                <h3 className="text-3xl font-bold text-slate-800 mb-4">Turn Insight Into Action</h3>
                <p className="text-slate-700 max-w-2xl mx-auto mb-2">Without leadership buy-in, culture change rarely sticks. The next step is a focused leadership conversation.</p>
                <p className="font-semibold text-slate-800">Book a free 30-minute Culture Pulse Check.</p>
              </div>
              <div className="bg-white rounded-lg p-6 mb-6 max-w-2xl mx-auto">
                <p className="font-medium text-slate-800 mb-3">We'll:</p>
                <ul className="space-y-2 text-slate-700">
                  {["Review your calculator results", "Identify your biggest culture or productivity pressure points", "Clarify where leadership alignment may be holding progress back", "Outline practical next steps that deliver value to decision-makers"].map((item, i) => (
                    <li key={i} className="flex items-start"><span className="flex-shrink-0 w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-green-700 text-sm font-bold mr-3 mt-0.5">✓</span><span>{item}</span></li>
                  ))}
                </ul>
              </div>
              <div className="text-center mb-6"><p className="text-slate-600 italic">No obligation. No generic advice. Just a clear conversation about what's realistic and commercially sensible.</p></div>
              <div className="text-center">
                <a href="https://calendly.com/ollie-kalobrate/kalobrate-introductions" target="_blank" rel="noopener noreferrer" data-testid="cta-button" className="text-white px-10 py-4 rounded-lg text-xl font-bold hover:opacity-90 transition-colors inline-block text-center shadow-lg" style={{backgroundColor: '#333333'}}>Book My Free Culture Pulse Check</a>
              </div>
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 p-6 rounded-lg mt-8">
              <p><strong>Methodology:</strong> Calculations based on industry averages from ONS labour market statistics, CIPD absence surveys, Mind workplace reports, and Deloitte economic impact studies. Individual results may vary based on organisation size, sector, and existing wellbeing provision.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default App;
